/**
 * Read a fetch() Response carrying an SSE stream and invoke `onEvent` with
 * each parsed `data:` payload. The single home for the byte-level framing
 * (reader/decoder/`\n\n`-split) — chat, build-progress, and publish streams
 * all consume through this. Server-side counterpart: `server/lib/sse.ts`.
 *
 * `onEvent` may return `true` to stop reading early (e.g. on a `done`
 * event); exceptions it throws propagate to the caller. Resolves `true`
 * when a handler requested the stop, `false` when the stream ended.
 *
 * `opts.stallMs`: the server writes a keepalive comment every 20s, so a
 * healthy connection is never silent for long. When set, a read that sees
 * NO bytes at all (data or keepalive) for stallMs throws an Error with
 * `stalled: true` — the signal that the pipe is dead, not that the turn
 * failed. Callers use it to switch to server-side recovery instead of
 * binning the turn.
 */
/**
 * The pipe went silent, as distinct from the turn failing. A real class rather
 * than `Object.assign(new Error(…), { stalled: true })` so callers can ask
 * `instanceof` — the `stalled` field stays, since that is what the existing
 * `err?.stalled` checks read (same shape, now also a type).
 */
export class StreamStalledError extends Error {
  readonly stalled = true;
}

/** True for anything carrying the stall marker, whoever threw it. */
export function isStalled(err: unknown): boolean {
  return err instanceof StreamStalledError || (!!err && typeof err === 'object' && 'stalled' in err && !!err.stalled);
}

export type SSEOptions = { stallMs?: number };

/** Return `true` to stop reading early. */
export type SSEHandler = (payload: unknown) => boolean | void;

export async function readSSE(res: Response, onEvent: SSEHandler, opts: SSEOptions = {}): Promise<boolean> {
  if (!res.body) throw new Error('The server sent no stream to read.');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const stallMs = opts.stallMs || 0;
  let buffer = '';
  while (true) {
    // Named off the reader rather than written out: @types/bun and the DOM lib
    // disagree about whether a done-result's `value` is optional, and this is
    // whichever one is actually in play.
    let result: Awaited<ReturnType<typeof reader.read>>;
    if (stallMs) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        result = await Promise.race([
          reader.read(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new StreamStalledError('Connection to the server stalled.')), stallMs);
          }),
        ]);
      } catch (err) {
        if (isStalled(err)) {
          try {
            reader.cancel();
          } catch {
            /* already closed */
          }
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    } else {
      result = await reader.read();
    }
    const { value, done } = result;
    if (done) return false;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      let payload: unknown;
      try {
        payload = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (onEvent(payload) === true) {
        try {
          reader.cancel();
        } catch {
          /* already closed */
        }
        return true;
      }
    }
  }
}
